/* eslint-disable complexity */
/**
 * Kit section reducer
 */

import _merge from "lodash/merge";
import _cloneDeep from "lodash/cloneDeep";
import _union from "lodash/union";
import { ItemType } from "@thenounproject/lingo-core";

import * as actionTypes from "../../actions/actionTypes";
import { clone, cloneDeep, without } from "lodash";

const sectionReducer = (state = {}, action) => {
  let sections;
  try {
    sections = action.response.entities.sections;
  } catch (error) {
    // That's ok
  }

  switch (action.type) {
    case actionTypes.MOVE_ITEMS_SUCCESS: {
      const { fromSectionUuid, toSectionUuid, itemUuids } = action;
      const newState = _cloneDeep(state);

      if (fromSectionUuid) {
        //If we are moving items from a section, we need to remove the items from the original section
        const versionedFromId = fromSectionUuid.concat("-0");
        const existingItems = clone(state[versionedFromId].items);
        const itemIds = itemUuids.map(uuid => uuid.concat("-0"));
        let newItems = existingItems;
        newItems = without(existingItems, ...itemIds);
        newState[versionedFromId].items = newItems;
        newState[versionedFromId].counts.items -= itemUuids.length;
      }
      const versionedToId = toSectionUuid.concat("-0");

      /**
       * Add the new item counts to the new section if it exists
       */
      if (newState[versionedToId]?.counts) {
        newState[versionedToId].counts.items += itemUuids.length;
      }

      return newState;
    }
    case actionTypes.DELETE_HEADING_WITH_ITEMS_SUCCESS: {
      const {
        entities: { items: deletedItems },
        result,
      } = action.response;
      const { sectionId } = deletedItems[result.items[0]];
      const versionedSectionId = `${sectionId}-0`;
      const section = state[versionedSectionId];

      const res = _merge({}, state, {
        [versionedSectionId]: {
          counts: {
            items: section.counts.items - result.items.length,
          },
        },
      });
      res[versionedSectionId].items = section.items.filter(item => !deletedItems[item]);
      res[versionedSectionId].headers = section.headers.filter(
        item => !deletedItems[`${item.id}-${item.version}`]
      );
      return res;
    }
    case actionTypes.REORDER_ITEMS_SUCCESS: {
      const { sectionId, reorderedSectionItemIds } = action;

      const newHeaders = state[sectionId].headers.map(header => {
        const reorderedItem = action.response.entities.items[`${header.id}-0`];
        if (reorderedItem) {
          return {
            ...header,
            displayOrder: reorderedItem.displayOrder,
          };
        } else return header;
      });
      newHeaders.sort((a, b) => a.displayOrder - b.displayOrder);

      return _merge({}, state, {
        [sectionId]: {
          items: reorderedSectionItemIds,
          headers: newHeaders,
        },
      });
    }
    case actionTypes.BATCH_SAVE_ITEMS_SUCCESS: {
      const {
        newItems,
        insertIndex,
        response: { entities, result },
      } = action;

      if (newItems.length === 0 || insertIndex === null) return state;

      const successfulNewItems = newItems
        .map(item => entities.items[`${item.uuid}-0`])
        .filter(Boolean);

      /**
       * If there are new items from the result of a batch save,
       * insert them into the section state.
       */
      const versionedSection = `${entities.items[result.items[0].item].sectionId}-0`,
        items = Array.from(state[versionedSection].items || []),
        headers = Array.from(state[versionedSection].headers || []);
      let indexCount = insertIndex;

      /**
       * Inject new item IDs into section.
       * If the item is a header, inject & sort.
       */
      successfulNewItems.forEach(item => {
        items.splice(indexCount, 0, `${item.id}-0`);
        indexCount += 1;

        if (item.type === ItemType.heading) {
          headers.push(formatHeader(item));
          headers.sort((a, b) => (a.displayOrder > b.displayOrder ? 1 : -1));
        }
      });

      // Update count
      const counts = { ...state[versionedSection].counts };
      counts.items += successfulNewItems.length;

      sections = {
        [versionedSection]: {
          items,
          headers,
          counts,
        },
      };
      return _merge({}, state, sections);
    }
    case actionTypes.INSERT_ASSET_ITEM_SUCCESS:
    case actionTypes.INSERT_COLOR_ASSET_ITEM_SUCCESS:
    case actionTypes.INSERT_CHUNKED_ASSET_ITEM_SUCCESS: {
      const itemId = action.response.result;
      const item = action.response.entities?.items?.[itemId];
      // Most likely a new asset was created in the library with no items
      if (!item) return state;
      const versionedSection = `${item.sectionId}-${item.version}`;

      // Inject item id into section
      const items = Array.from(state[versionedSection].items);
      const { insertIndex, displayOrder } = action.insertPosition;

      if (displayOrder === "append") {
        items.push(itemId);
      } else if (displayOrder === "prepend") {
        items.unshift(itemId);
        /**
         * If we're able to use a specific insertIndex value,
         * use that to avoid having to parse the index.
         */
      } else if (insertIndex !== undefined) {
        items.splice(insertIndex, 0, itemId);
      } else {
        /**
         * If no insertIndex is provided, parse the index from the
         * displayOrder sent to the API.
         */
        const [direction, targetId] = displayOrder.split(":");
        let spliceIndex = items.indexOf(`${targetId}-0`);
        if (direction === "after") spliceIndex++;
        items.splice(spliceIndex, 0, itemId);
      }

      // If it's a header, inject in header arr & sort by display order
      let headers = [];
      if (item.type === ItemType.heading) {
        headers = Array.from(state[versionedSection].headers || []);
        headers.push(formatHeader(item));
        headers.sort((a, b) => (a.displayOrder > b.displayOrder ? 1 : -1));
      }

      // Update count
      const counts = { ...state[versionedSection].counts };
      counts.items += 1;

      sections = {
        [versionedSection]: {
          items,
          headers,
          counts,
        },
      };
      return _merge({}, state, sections);
    }
    case actionTypes.BATCH_UPDATE_ITEM_STATUS_SUCCESS: {
      if (!action.response.entities.items) return state;

      const successfulItems = action.response.result.items.filter(i => i.success).map(i => i.item);
      if (!successfulItems.length) return state;

      const newState = { ...state };
      const { entities } = action.response;
      let changed = false;
      successfulItems.forEach(versionedId => {
        const fullItem = entities.items[versionedId],
          { sectionId, version, status, displayOrder, type: itemType } = fullItem,
          versionedSection = `${sectionId}-${version}`,
          itemSection = { ...newState[versionedSection] };

        // Remove items
        if (status === "trashed" || status === "deleted") {
          const newItems = itemSection.items.filter(i => versionedId !== i);
          if (newItems.length !== itemSection.items.length) {
            itemSection.items = newItems;
            changed = true;
          }
        }

        // Add items
        if (status === "active") {
          const newItems = Array.from(itemSection.items || []);
          const insertIndex = newItems.findIndex(i => i.displayOrder >= displayOrder);
          newItems.splice(insertIndex === -1 ? newItems.length : insertIndex, 0, versionedId);
          itemSection.items = [...new Set(newItems)];
          changed = true;
        }

        // Update count
        if (changed) {
          const newCounts = { ...itemSection.counts };
          newCounts.items += status === "active" ? 1 : -1;
          itemSection.counts = newCounts;
        }

        // If header, remove from header arr
        if (status === "deleted" && itemType === ItemType.heading) {
          let newHeaders = Array.from(itemSection.headers || []);
          newHeaders = newHeaders.filter(h => h.id !== fullItem.id);
          if (newHeaders.length !== itemSection.headers.length) itemSection.headers = newHeaders;
        }

        newState[versionedSection] = itemSection;
      });

      return changed ? newState : state;
    }
    case actionTypes.SAVE_ITEM_DATA_SUCCESS: {
      const item = action.response.entities.items[action.response.result];

      /**
       * For updating the heading value in kitNav when a user edits
       */
      if (item.type === ItemType.heading) {
        const versionedSection = `${item.sectionId}-${item.version}`,
          headingIndex = state[versionedSection].headers.findIndex(h => h.shortId === item.shortId),
          headers = new Array(state[versionedSection].headers.length);
        headers[headingIndex] = { name: item.data.textContent };
        sections = {
          [versionedSection]: {
            headers,
          },
        };
        return _merge({}, state, sections);
      }

      return state;
    }

    case actionTypes.FETCH_KIT_OUTLINE_SUCCESS: {
      const newState = cloneDeep(_merge({}, state, sections));
      if (!sections) return newState;
      // Force update of headers
      Object.keys(sections).forEach(sectionId => {
        newState[sectionId].headers = sections[sectionId].headers;
      });
      return newState;
    }
    case actionTypes.FETCH_SECTION_SUCCESS: {
      const versionId = action.response.result;

      if (sections[versionId]) {
        const stateAssets = state[versionId] ? state[versionId].assets : [],
          stateItems = action.page !== 1 && state[versionId] ? state[versionId].items : [];

        sections[versionId].assets = _union(
          stateAssets,
          sections[versionId].items.filter(i => {
            return action.response.entities.items[i].type === "asset";
          })
        );
        sections[versionId].items = _union(stateItems, sections[versionId].items);
      }
      return _merge({}, state, sections);
    }
    case actionTypes.RESET_SECTION: {
      const versionId = action.sectionId + "-0";
      const newState = cloneDeep(state);
      if (newState[versionId]) {
        newState[versionId].items = [];
        newState[versionId].assets = [];
      }
      return newState;
    }
    case actionTypes.SORT_SECTION_ITEMS:
      return _merge({}, state, action.sections);
    case actionTypes.SEARCH_KIT_SUCCESS:
    default: {
      if (sections) {
        return _merge({}, state, sections);
      }
      return state;
    }
  }
};

function formatHeader(data) {
  return {
    id: data.id,
    shortId: data.shortId,
    name: data.data.textContent,
    version: data.version,
    displayOrder: data.displayOrder,
  };
}

export default sectionReducer;
